import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useEffect, useRef, useState } from "react";
import {
  HiOutlineFlag,
  HiOutlineIdentification,
  HiOutlineInformationCircle,
  HiOutlineLightningBolt,
  HiOutlineTruck,
} from "react-icons/hi";
import { useSelector } from "react-redux";
import {
  Button,
  Header,
  Icon,
  Input,
  List,
  Progress,
  Tab,
} from "semantic-ui-react";
import { getTextByValueAndKey } from "../../api/keyvalue";
import { fetchDataById } from "../../api/requestCreation";
import { vehicleByVehNo } from "../../api/vehicleapi";
import {
  getAllGpsOfflineVehicleData,
  getAllVehicleInfo,
  getVehicleFinanceData,
  getVehicleJobProgress,
  getVehicleLiveInfoByNo,
  getVehicleLiveStats,
  getFleetIssueWoDashboardStats
} from "../../api/vehicleLiveInfoApi";
import LoadingSpinner from "../../components/LoadingSpinner";
import FleetDashboardMap from "../../components/maps/Fleet/FleetDashboardMap";
import { formatFromJsonDate } from "../../dateConfig";
import useUserCompany from "../../hooks/useUserCompany";
import { CornerLeftUp, Filter } from "react-feather";
import Modal from "../../components/Modal";

const FleetDashboard = () => {
  const user = useSelector((state) => state.user);
  const userCompany = useUserCompany();
  const userCurrencyName = userCompany ? userCompany.comCurrencyName : null;
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const wrapperRef = useRef(null);
  const chartRef = useRef(null);
  const [vehicleLiveInfo, setVehicleLiveInfo] = useState([]);
  const [selectedVehicleLiveInfo, setSelectedVehicleLiveInfo] = useState([]);
  const [selectedVehicleJob, setSelectedVehicleJob] = useState([]);
  const [allVehicleStats, setAllVehicleStats] = useState([]);
  const [gpsOfflineData, setGpsOfflineData] = useState([]);
  const [popupVehicle, setPopupVehicle] = useState(null);
  const [jobDetails, setJobDetails] = useState([]);
  const [vehicleDetails, setVehicleDetails] = useState([]);
  const [vehicleFilter, setVehicleFilter] = useState("all"); // all | moving | stopped | onJob
  const vehicleRefs = useRef({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSelectLoading, setIsSelectLoading] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalRef = useRef(null);

  const [financeVehicleNos, setFinanceVehicleNos] = useState("");
  const [financeSummaryData, setFinanceSummaryData] = useState([]);
  const [vehicleFinanceData, setVehicleFinanceData] = useState([]);
  const [isFinanceLoading, setIsFinanceLoading] = useState(false);
  const [isFinanceTableExpanded, setIsFinanceTableExpanded] = useState(false);
  const [issueWoStats, setIssueWoStats] = useState({ issue: null, workOrder: null });
  const [isIssueWoStatsLoading, setIsIssueWoStatsLoading] = useState(false);

  const [selectedVehicleFinanceData, setSelectedVehicleFinanceData] = useState(
    [],
  );
  const [isSelectedVehicleFinanceView, setIsSelectedVehicleFinanceView] =
    useState(false);
  const [isSelectedVehicleFinanceLoading, setIsSelectedVehicleFinanceLoading] =
    useState(false);

  const startLocationObj = jobDetails?.requestLocationsResponses?.find(
    (loc) => loc.rlkLocationType === 1,
  );
  const endLocationObj = jobDetails?.requestLocationsResponses?.find(
    (loc) => loc.rlkLocationType === 2,
  );

  const startLocation = startLocationObj?.locationName ?? "N/A";
  const endLocation = endLocationObj?.locationName ?? "N/A";

  const [filterText, setFilterText] = useState(""); // <-- state for the filter

  // Filter by vehicleNo (case-insensitive). Change .vehicleNo to any other field.
  const filteredData = gpsOfflineData.filter((row) =>
    row.vehicleNo.toLowerCase().includes(filterText.toLowerCase()),
  );

  useEffect(() => {
    const resizeChart = async () => {
      if (!selectedVehicle && chartRef.current) {
        setTimeout(() => {
          chartRef.current?.getEchartsInstance?.()?.resize();
        }, 100);
      }
    };
    console.log("moving: ", selectedVehicle, movingVehicles);
    resizeChart(); // call it
  }, [selectedVehicle]);

  useEffect(() => {
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    const formattedOneYearAgo = oneYearAgo.toISOString().split("T")[0];
    const formattedToday = new Date().toISOString().split("T")[0];

    setFromDate(formattedOneYearAgo);
    setToDate(formattedToday);
  }, []);

  useEffect(() => {
    const fetchVehicleLiveInfo = async () => {
      if (selectedVehicle) {
        setIsSelectLoading(true);

        // const financeData = await getVehicleFinanceData({
        //   fromDate: fromDate,
        //   toDate: toDate,
        //   vehicleNo: selectedVehicle.vehicleNo,
        //   user: user.username,
        //   company: userCompany ? userCompany.comId : null,
        // });
        // console.log("selected financeData", financeData);

        const response = await getVehicleLiveInfoByNo(
          selectedVehicle.vehicleNo,
        );
        setSelectedVehicleLiveInfo(response[0] || {});

        const progress = await getVehicleJobProgress(selectedVehicle.vehicleNo);
        setSelectedVehicleJob(progress || []);

        const vehicle = await vehicleByVehNo(
          selectedVehicle.vehicleNo,
          userCompany ? userCompany.comId : null,
        );
        let fuelTypeValue = "";
        if (vehicle?.vehicleDetail?.vehFuelType) {
          fuelTypeValue = await getTextByValueAndKey(
            "fuel_type",
            vehicle?.vehicleDetail?.vehFuelType,
          );
        }
        setVehicleDetails({
          ...vehicle.vehicleDetail,
          fuelTypeValue: fuelTypeValue,
        });

        if (progress && selectedVehicle.onJob === true) {
          const requestData = await fetchDataById(progress[0]?.jobRequest);
          setJobDetails(requestData || []);
        }
        setIsSelectLoading(false);
      }
    };

    fetchVehicleLiveInfo();
  }, [selectedVehicle]);

  function createData(
    vehicleNo,
    terminalId,
    dateTime,
    longitude,
    latitude,
    speed,
    fuelLevel,
    direction,
    mileage,
    gpsServiceProvider,
    cargoTemp,
    engineHours,
    batteryVoltage,
    accelMax,
    brakeMax,
    ignition,
    onJob,
    vehTransporter,
  ) {
    return {
      vehicleNo,
      terminalId,
      dateTime,
      longitude,
      latitude,
      speed,
      fuelLevel,
      direction,
      mileage,
      gpsServiceProvider,
      cargoTemp,
      engineHours,
      batteryVoltage,
      accelMax,
      brakeMax,
      ignition,
      onJob,
      vehTransporter,
    };
  }

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      // const financeData = await getVehicleFinanceData({
      //   fromDate: fromDate,
      //   toDate: toDate,
      //   vehicleNo: "",
      //   user: user.username,
      //   company: userCompany ? userCompany.comId : null,
      // });
      // console.log("financeData", financeData);
      // setVehicleFinanceData(financeData || []);

      const vehicleInfoData = await getAllVehicleInfo(user.username);
      setVehicleLiveInfo(vehicleInfoData || []);

      const infoStatData = await getVehicleLiveStats();
      setAllVehicleStats(infoStatData || []);

      const offlineData = await getAllGpsOfflineVehicleData();
      console.log("gpsOfflineData", offlineData);
      const mappedData = [];
      if (offlineData && offlineData.length > 0) {
        offlineData.forEach((item) => {
          mappedData.push(
            createData(
              item.vehicleNo,
              item.terminalId,
              formatFromJsonDate(item.dateTime),
              item.longitude,
              item.latitude,
              item.speed,
              item.fuelLevel,
              item.direction,
              item.mileage,
              item.gpsServiceProvider,
              item.cargoTemp,
              item.engineHours,
              item.batteryVoltage,
              item.accelMax,
              item.brakeMax,
              item.ignition,
              item.onJob,
              item.vehTransporter,
            ),
          );
        });
      }
      setGpsOfflineData(mappedData);

      setIsIssueWoStatsLoading(true);
      try {
        const statsData = await getFleetIssueWoDashboardStats();
        setIssueWoStats({
          issue:      statsData?.find((r) => r.category === "ISSUE")       || null,
          workOrder:  statsData?.find((r) => r.category === "WORK_ORDER")  || null,
        });
      } catch (e) {
        console.error("Failed to load issue/wo stats", e);
      } finally {
        setIsIssueWoStatsLoading(false);
      }

      setIsLoading(false);
    })();
  }, []);

  const fetchSelectedVehicleFinanceData = async (vehicleNo) => {
    if (!fromDate || !toDate || !user?.username || !vehicleNo) return;

    setIsSelectedVehicleFinanceLoading(true);

    try {
      const financeData = await getVehicleFinanceData({
        fromDate,
        toDate,
        vehicleNo,
        user: user.username,
        company: userCompany ? userCompany.comId : null,
      });

      setSelectedVehicleFinanceData(financeData || []);
      setIsSelectedVehicleFinanceView(true);
    } catch (error) {
      console.error("Error loading selected vehicle finance data", error);
      setSelectedVehicleFinanceData([]);
      setIsSelectedVehicleFinanceView(true);
    } finally {
      setIsSelectedVehicleFinanceLoading(false);
    }
  };

  const fetchFinanceSummaryData = async () => {
    if (!fromDate || !toDate || !user?.username) return;

    setIsFinanceLoading(true);

    try {
      const financeData = await getVehicleFinanceData({
        fromDate,
        toDate,
        vehicleNo: "",
        user: user.username,
        company: userCompany ? userCompany.comId : null,
      });

      setFinanceSummaryData(financeData || []);
    } catch (error) {
      console.error("Error loading finance summary data", error);
      setFinanceSummaryData([]);
    } finally {
      setIsFinanceLoading(false);
    }
  };

  useEffect(() => {
    if (!fromDate || !toDate || !user?.username) return;

    fetchFinanceSummaryData();
  }, [fromDate, toDate, user?.username, userCompany]);

  const fetchVehicleFinanceTableData = async () => {
    if (!fromDate || !toDate || !user?.username) return;

    setIsFinanceLoading(true);

    try {
      const financeData = await getVehicleFinanceData({
        fromDate,
        toDate,
        vehicleNo: financeVehicleNos.trim(),
        user: user.username,
        company: userCompany ? userCompany.comId : null,
      });

      setVehicleFinanceData(financeData || []);
    } catch (error) {
      console.error("Error loading vehicle finance table data", error);
      setVehicleFinanceData([]);
    } finally {
      setIsFinanceLoading(false);
    }
  };

  const handleCollapseFinanceTable = () => {
    setIsFinanceTableExpanded(false);
  };

  const handleApplyFinanceDateFilter = async () => {
    if (isFinanceTableExpanded) {
      await fetchVehicleFinanceTableData();
    } else {
      await fetchFinanceSummaryData();
    }
  };

  const handleApplyVehicleFinanceTableFilter = async () => {
    await fetchVehicleFinanceTableData();
  };

  const financeSummaryChartData = [...financeSummaryData]
    .sort((a, b) => Number(b.totCost || 0) - Number(a.totCost || 0))
    .slice(0, 30);

  const financeChartRawData = [
    financeSummaryChartData.map((row) => Number(row.chhCost || 0)),
    financeSummaryChartData.map((row) => Number(row.deductions || 0)),
    financeSummaryChartData.map((row) => Number(row.chhMaintenance || 0)),
  ];

  const financeChartLabels = ["Income", "Deductions", "Maintenance"];

  const financeChartTotalData = [];

  if (financeChartRawData.length > 0 && financeChartRawData[0].length > 0) {
    for (let i = 0; i < financeChartRawData[0].length; i++) {
      let sum = 0;

      for (let j = 0; j < financeChartRawData.length; j++) {
        sum += financeChartRawData[j][i];
      }

      financeChartTotalData.push(sum);
    }
  }

  const issueWoStatRows = [
    {
      title: "Issues",
      data: issueWoStats.issue,
    },
    {
      title: "Work Orders",
      data: issueWoStats.workOrder,
    },
  ];

  const issueWoStatusCards = [
    { key: "openCount",        label: "Open",              color: "#3B82F6" },
    { key: "overdueCount",     label: "Overdue",           color: "#EF4444" },
    { key: "inProgressCount",  label: "In Progress",       color: "#F59E0B" },
    { key: "onHoldCount",      label: "On Hold",           color: "#8B5CF6" },
    { key: "resolvedCount",    label: "Resolved",          color: "#10B981" },
    { key: "closedThisMonth",  label: "Closed This Month", color: "#6B7280" },
  ];

  const financeSummaryChartOptions = {
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
      formatter: (params) => {
        const vehicleNo = params?.[0]?.axisValue || "N/A";
        const vehicleIndex = params?.[0]?.dataIndex ?? 0;

        let tooltip = `<strong>${vehicleNo}</strong><br/>`;

        params.forEach((item) => {
          const actualValue =
            financeChartRawData[item.seriesIndex][vehicleIndex];
          const percentage = Number(item.value || 0) * 100;

          tooltip += `
          ${item.marker}
          ${item.seriesName}: ${formatAmount(actualValue, false)}
          (${percentage.toFixed(1)}%)<br/>
        `;
        });

        tooltip += `<br/><strong>Total Balance: ${formatAmount(
          financeChartTotalData[vehicleIndex] || 0,
          false,
        )}</strong>`;

        return tooltip;
      },
    },

    legend: {
      top: 0,
      selectedMode: false,
    },

    grid: {
      left: "3%",
      right: "4%",
      bottom: "10%",
      top: "12%",
      containLabel: true,
    },

    yAxis: {
      type: "value",
      min: 0,
      max: 1,
      axisLabel: {
        formatter: (value) => `${Math.round(value * 100)}%`,
      },
    },

    xAxis: {
      type: "category",
      data: financeSummaryChartData.map((row) => row.vehVehicleno || "N/A"),
      axisLabel: {
        rotate: 35,
      },
    },

    series: financeChartLabels.map((name, seriesIndex) => ({
      name,
      type: "bar",
      stack: "total",
      barWidth: "60%",
      label: {
        show: true,
        formatter: (params) => {
          const percentage = Number(params.value || 0) * 100;

          if (percentage <= 0) return "";

          return `${percentage.toFixed(1)}%`;
        },
      },
      data: financeChartRawData[seriesIndex].map((value, dataIndex) =>
        financeChartTotalData[dataIndex] <= 0
          ? 0
          : value / financeChartTotalData[dataIndex],
      ),
    })),
  };

  const formatAmount = (value, showCurrency = true) => {
    const numberValue = Number(value || 0);

    const formattedValue = numberValue.toLocaleString("en-LK", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return showCurrency && userCurrencyName
      ? `${userCurrencyName} ${formattedValue}`
      : formattedValue;
  };

  const handleOpenFinanceModal = async () => {
    console.log("Opening finance modal with vehicleNos:");
    setIsModalOpen(true);
    modalRef.current?.showModal();

    if (vehicleFinanceData.length === 0) {
      await fetchVehicleFinanceTableData();
    }
  };

  const handleCloseFinanceModal = () => {
    setIsModalOpen(false);
    modalRef.current?.close();
  };

  const handleResetVehicleFinanceModalFilter = async () => {
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    const formattedOneYearAgo = oneYearAgo.toISOString().split("T")[0];
    const formattedToday = new Date().toISOString().split("T")[0];

    setFromDate(formattedOneYearAgo);
    setToDate(formattedToday);
    setFinanceVehicleNos("");
    setIsSelectedVehicleFinanceView(false);
    setSelectedVehicleFinanceData([]);

    setIsFinanceLoading(true);

    try {
      const financeData = await getVehicleFinanceData({
        fromDate: formattedOneYearAgo,
        toDate: formattedToday,
        vehicleNo: "",
        user: user.username,
        company: userCompany ? userCompany.comId : null,
      });

      setVehicleFinanceData(financeData || []);
      setFinanceSummaryData(financeData || []);
    } catch (error) {
      console.error("Error resetting vehicle finance filter", error);
      setVehicleFinanceData([]);
    } finally {
      setIsFinanceLoading(false);
    }
  };

  // Moving vehicles only
  const movingVehicles = vehicleLiveInfo.filter(
    (v) => v.speed > 0 && v.latitude && v.longitude,
  );

  const getVehicleIconColor = (vehicle) => {
    if (vehicle.onJob) return "blue";
    if (vehicle.speed > 0) return "green"; // moving
    if (vehicle.ignition) return "orange"; // idle (ignition on, not moving)
    return "red"; // parked (ignition off, not moving)
  };

  const movingCount = vehicleLiveInfo.filter((v) => v.speed > 0).length;
  const idlingCount = vehicleLiveInfo.filter(
    (v) => v.speed === 0 && v.ignition === 1,
  ).length;
  const stoppedCount = vehicleLiveInfo.filter(
    (v) => v.speed === 0 && v.ignition === 0,
  ).length;
  const onJobCount = vehicleLiveInfo.filter((v) => v.onJob).length;

  const vehicleStatusOptions = {
    tooltip: { trigger: "item" },
    legend: { bottom: 10, left: "center" },
    series: [
      {
        name: "Vehicle Status",
        type: "pie",
        radius: ["40%", "65%"],
        itemStyle: {
          borderRadius: 6,
          borderColor: "#fff",
          borderWidth: 2,
        },
        label: {
          formatter: "{b}: {d}%",
          fontSize: 14,
        },
        data: [
          { value: movingCount, name: "Moving" },
          { value: idlingCount, name: "Idling" },
          { value: stoppedCount, name: "Stopped" },
          { value: onJobCount, name: "On Job" },
        ],
      },
    ],
  };

  const topMileageVehicles = vehicleLiveInfo
    .filter((v) => v.mileage != null)
    .sort((a, b) => b.mileage - a.mileage)
    .slice(0, 5);

  const mileageOptions = {
    tooltip: { trigger: "axis" },
    grid: { left: "20%", right: "10%", bottom: "10%", top: "10%" },
    xAxis: { type: "value" },
    yAxis: {
      type: "category",
      data: topMileageVehicles.map((v) => v.vehicleNo),
    },
    series: [
      {
        type: "bar",
        data: topMileageVehicles.map((v) => v.mileage),
        barWidth: "50%",
        itemStyle: {
          borderRadius: [0, 6, 6, 0],
        },
      },
    ],
  };

  const speedBuckets = [
    { range: "0", count: vehicleLiveInfo.filter((v) => v.speed === 0).length },
    {
      range: "1-20",
      count: vehicleLiveInfo.filter((v) => v.speed > 0 && v.speed <= 20).length,
    },
    {
      range: "21-40",
      count: vehicleLiveInfo.filter((v) => v.speed > 20 && v.speed <= 40)
        .length,
    },
    {
      range: "41-60",
      count: vehicleLiveInfo.filter((v) => v.speed > 40 && v.speed <= 60)
        .length,
    },
    { range: "61+", count: vehicleLiveInfo.filter((v) => v.speed > 60).length },
  ];

  const speedDistributionOptions = {
    tooltip: { trigger: "axis" },
    xAxis: {
      type: "category",
      name: "Speed Range (km/h)",
      data: speedBuckets.map((b) => b.range),
    },
    yAxis: { type: "value" },
    series: [
      {
        type: "line",
        data: speedBuckets.map((b) => b.count),
        areaStyle: {
          color: "#86efac",
        },
        smooth: true,
        symbol: "circle",
        name: "Number of Vehicles",
        lineStyle: { width: 2, color: "#16a34a" },
      },
    ],
  };

  const selectedVehicleFinanceCards = [
    {
      label: "Vehicle No",
      value: selectedVehicle?.vehicleNo || "N/A",
    },
    {
      label: "No. of Jobs",
      value: selectedVehicleFinanceData.reduce(
        (sum, row) => sum + Number(row.total || 0),
        0,
      ),
    },
    {
      label: `Income${userCurrencyName ? ` (${userCurrencyName})` : ""}`,
      value: formatAmount(
        selectedVehicleFinanceData.reduce(
          (sum, row) => sum + Number(row.chhCost || 0),
          0,
        ),
        false,
      ),
    },
    {
      label: `Deductions${userCurrencyName ? ` (${userCurrencyName})` : ""}`,
      value: formatAmount(
        selectedVehicleFinanceData.reduce(
          (sum, row) => sum + Number(row.deductions || 0),
          0,
        ),
        false,
      ),
    },
    {
      label: `Maintenance${userCurrencyName ? ` (${userCurrencyName})` : ""}`,
      value: formatAmount(
        selectedVehicleFinanceData.reduce(
          (sum, row) => sum + Number(row.chhMaintenance || 0),
          0,
        ),
        false,
      ),
    },
    {
      label: `Balance${userCurrencyName ? ` (${userCurrencyName})` : ""}`,
      value: formatAmount(
        selectedVehicleFinanceData.reduce(
          (sum, row) => sum + Number(row.totCost || 0),
          0,
        ),
        false,
      ),
    },
  ];

  const financeSummaryCards = [
    {
      label: "No. of Vehicles",
      value: new Set(
        financeSummaryData.map((row) => row.vehVehicleno).filter(Boolean),
      ).size,
    },
    {
      label: "No. of Jobs",
      value: financeSummaryData.reduce(
        (sum, row) => sum + Number(row.total || 0),
        0,
      ),
    },
    {
      label: `Income${userCurrencyName ? ` (${userCurrencyName})` : ""}`,
      value: formatAmount(
        financeSummaryData.reduce(
          (sum, row) => sum + Number(row.chhCost || 0),
          0,
        ),
        false,
      ),
    },
    {
      label: `Deductions${userCurrencyName ? ` (${userCurrencyName})` : ""}`,
      value: formatAmount(
        financeSummaryData.reduce(
          (sum, row) => sum + Number(row.deductions || 0),
          0,
        ),
        false,
      ),
    },
    {
      label: `Maintenance${userCurrencyName ? ` (${userCurrencyName})` : ""}`,
      value: formatAmount(
        financeSummaryData.reduce(
          (sum, row) => sum + Number(row.chhMaintenance || 0),
          0,
        ),
        false,
      ),
    },
    {
      label: `Balance${userCurrencyName ? ` (${userCurrencyName})` : ""}`,
      value: formatAmount(
        financeSummaryData.reduce(
          (sum, row) => sum + Number(row.totCost || 0),
          0,
        ),
        false,
      ),
    },
  ];

  const handleCancelSelectedVehicleFinanceView = () => {
    setIsSelectedVehicleFinanceView(false);
    setSelectedVehicleFinanceData([]);
  };

  const panes = [
    {
      menuItem: {
        key: "flag",
        icon: <HiOutlineFlag />,
        content: "Flag",
      },
      render: () => (
        <Tab.Pane
          attached={false}
          style={{ margin: "10px", overflowY: "auto", maxHeight: "90%" }}
        >
          <Header
            as="h4"
            className={"ui dividing header"}
            style={{ marginBottom: 12 }}
          >
            Vehicle Metrics
          </Header>
          <div
            style={{
              background: "#fff",
              padding: "16px",
              borderRadius: "12px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "16px",
              }}
            >
              {[
                {
                  label: "Speed",
                  value: selectedVehicleLiveInfo?.speed
                    ? `${selectedVehicleLiveInfo.speed} km/h`
                    : "--",
                },
                {
                  label: "Battery",
                  value: selectedVehicle?.batteryVoltage || "--",
                },
                {
                  label: "Mileage",
                  value: selectedVehicle?.mileage
                    ? `${selectedVehicle.mileage} km`
                    : "--",
                },
                {
                  label: "Fuel Used",
                  value: selectedVehicle?.fuelLevel
                    ? `${selectedVehicle.fuelLevel} L`
                    : "--",
                },
                {
                  label: "Engine Hrs",
                  value: selectedVehicle?.engineHours || "--",
                },
                {
                  label: "Accel Max",
                  value: selectedVehicle?.accelMax || "--",
                },
                {
                  label: "Brake Max",
                  value: selectedVehicle?.brakeMax || "--",
                },
                {
                  label: "Ignition",
                  value: selectedVehicle?.ignition ? "ON" : "OFF",
                },
              ].map((card, i) => (
                <div
                  key={i}
                  style={{
                    backgroundColor: "#f9fafb",
                    borderRadius: "8px",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                    padding: "16px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {card.value}
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#4b5563",
                      marginTop: "4px",
                    }}
                  >
                    {card.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Tab.Pane>
      ),
    },
    {
      menuItem: {
        key: "status",
        icon: <HiOutlineInformationCircle />,
        content: "Status",
        disabled: selectedVehicle?.onJob === false,
      },
      render: () => (
        <Tab.Pane
          attached={false}
          style={{ margin: 10, overflowY: "auto", maxHeight: "90%" }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "16px",
              borderRadius: "12px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
              height: "100%",
            }}
          >
            {/* Status: Good Section moved here */}
            <div
              style={{
                backgroundColor: "#f9fafb",
                // padding: "16px",
                borderRadius: "12px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                marginTop: "16px",
              }}
            >
              <Header
                as="h4"
                className={"ui dividing header"}
                style={{ marginBottom: 8 }}
              >
                Job Status
              </Header>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <HiOutlineLightningBolt color="green" size={20} />
                <strong style={{ marginLeft: 8, color: "green" }}>
                  {selectedVehicleJob[0]?.stsDescription || ""}
                </strong>
              </div>
              <Progress
                percent={selectedVehicleJob[0]?.progressPercentage || 0}
                color="green"
                size="small"
              />
              <p
                style={{
                  fontSize: "12px",
                  textAlign: "center",
                  color: "#6B7280",
                  marginTop: 8,
                }}
              >
                {selectedVehicleJob[0]?.progressPercentage || 0}%
              </p>
            </div>
            {jobDetails && (
              <div
                style={{
                  backgroundColor: "#fff",
                  padding: "16px",
                  borderRadius: "12px",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  marginTop: "16px",
                }}
              >
                <h4 className="ui dividing header">Request Information</h4>

                <p>
                  <span className="label">Request Code:</span>{" "}
                  <span className="ui label">
                    {jobDetails.reqCode || "N/A"}
                  </span>
                </p>
                <p>
                  <span className="label">Job ID:</span>{" "}
                  <span className="ui label">
                    {selectedVehicleJob[0]?.jobCode || "N/A"}
                  </span>
                </p>
                <p>
                  <span className="label">Request By:</span>{" "}
                  <span className="ui label">
                    {jobDetails.reqCreatedBy || "N/A"}
                  </span>
                </p>
                <p>
                  <span className="label">Request Date:</span>{" "}
                  <span className="ui label">
                    {jobDetails.reqCreatedDate
                      ? formatFromJsonDate(jobDetails.reqCreatedDate)
                      : "N/A"}
                  </span>
                </p>
                <p>
                  <span className="label">Approved By:</span>{" "}
                  <span className="ui label">{jobDetails.reqApprovedBy}</span>
                </p>
                <p>
                  <span className="label">Approved Date:</span>{" "}
                  <span className="ui label">
                    {jobDetails.reqApprovedDate
                      ? formatFromJsonDate(jobDetails.reqApprovedDate)
                      : "N/A"}
                  </span>
                </p>
                <p>
                  <span className="label">Start location:</span>{" "}
                  <span className="ui label" style={{ textAlign: "right" }}>
                    {startLocation || "N/A"}
                  </span>
                </p>
                <p>
                  <span className="label">End Location:</span>{" "}
                  <span className="ui label" style={{ textAlign: "right" }}>
                    {endLocation || "N/A"}
                  </span>
                </p>
              </div>
            )}
          </div>
        </Tab.Pane>
      ),
    },
    {
      menuItem: {
        key: "vehicle",
        icon: <HiOutlineIdentification />,
        content: "Vehicle",
      },
      render: () => (
        <Tab.Pane attached={false} style={{ margin: 10 }}>
          <div
            style={{
              backgroundColor: "#fff",
              padding: 20,
              borderRadius: 10,
              boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
              maxWidth: 400,
            }}
          >
            <h4 className="ui dividing header">Vehicle Information</h4>

            <div style={{ marginTop: 10 }}>
              <p>
                <strong>Vehicle Code:</strong>{" "}
                <span className={"ui label"}>
                  {vehicleDetails?.vehCode || "N/A"}
                </span>
              </p>
              <p>
                <strong>Engine Capacity:</strong>{" "}
                <span className="ui label">
                  {vehicleDetails?.vehEngineCapacity || "N/A"}
                </span>
              </p>
              <p>
                <strong>Chassis No:</strong>{" "}
                <span className="ui label">
                  {vehicleDetails?.vehChassisNo || "N/A"}
                </span>
              </p>
              <p>
                <strong>Book Reg No:</strong>{" "}
                <span className="ui label">
                  {vehicleDetails?.vehBookRegNo || "N/A"}
                </span>
              </p>
              <p>
                <strong>Manufactured Year:</strong>{" "}
                <span className="ui label">
                  {vehicleDetails?.vehYearManufacturer || "N/A"}
                </span>
              </p>
              <p>
                <strong>Registered Year:</strong>{" "}
                <span className="ui label">
                  {vehicleDetails?.vehYearRegister || "N/A"}
                </span>
              </p>
              <p>
                <strong>Vehicle No:</strong>{" "}
                <span className="ui label">
                  {vehicleDetails?.vehVehicleNo || "N/A"}
                </span>
              </p>
              <p>
                <strong>Fuel Type:</strong>{" "}
                <span className="ui label">
                  {vehicleDetails?.fuelTypeValue || "N/A"}
                </span>
              </p>
              <p>
                <strong>Manufacturer:</strong>{" "}
                <span className="ui label">
                  {vehicleDetails?.vehManufacturerName || "N/A"}
                </span>
              </p>
            </div>
          </div>
        </Tab.Pane>
      ),
    },
  ];

  return (
    <div
      ref={wrapperRef}
      style={{
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
        }}
       >
        {/* Left Sidebar */}
        <div
          style={{
            minWidth: "280px",
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
            height: "75vh",
            borderRadius: "4px",
            margin: "4px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
          }}
        >
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <div style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                <Header as="h4" style={{ marginBottom: 4 }}>
                  Live Tracking
                </Header>
                <p style={{ color: "#6B7280", marginBottom: 10 }}>
                  {movingVehicles.length} vehicles on road
                </p>
                <div>
                  <Input
                    icon="search"
                    placeholder="Search vehicles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    fluid
                  />

                  {/* Filter Pills */}
                  <div
                    style={{
                      marginTop: 10,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    {[
                      { key: "all", label: "All" },
                      { key: "moving", label: "Moving" },
                      { key: "stopped", label: "Stopped" },
                      { key: "onJob", label: "On Job" },
                    ].map(({ key, label }) => (
                      <div
                        key={key}
                        onClick={() => setVehicleFilter(key)}
                        style={{
                          padding: "6px 12px",
                          borderRadius: "16px",
                          backgroundColor:
                            vehicleFilter === key ? "#2563EB" : "#f3f4f6",
                          color: vehicleFilter === key ? "#fff" : "#374151",
                          fontSize: 12,
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                      >
                        {label}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  padding: "0 8px",
                }}
              >
                <List selection>
                  {vehicleLiveInfo
                    .filter((v) => {
                      const matchesSearch = v.vehicleNo
                        ?.toLowerCase()
                        .includes(searchQuery.toLowerCase());

                      const matchesFilter =
                        vehicleFilter === "all" ||
                        (vehicleFilter === "moving" && v.speed > 0) ||
                        (vehicleFilter === "stopped" &&
                          v.speed === 0 &&
                          !v.ignition) ||
                        (vehicleFilter === "onJob" && v.onJob);

                      return matchesSearch && matchesFilter;
                    })
                    .map((v) => (
                      <List.Item
                        key={v.vehicleNo}
                        onClick={async () => {
                          if (selectedVehicle?.vehicleNo === v.vehicleNo) {
                            setSelectedVehicle(null);
                            setSelectedVehicleFinanceData([]);
                            setIsSelectedVehicleFinanceView(false);
                            return;
                          }

                          setSelectedVehicle(v);
                          await fetchSelectedVehicleFinanceData(v.vehicleNo);
                        }}
                        style={{
                          backgroundColor:
                            selectedVehicle?.vehicleNo === v.vehicleNo
                              ? "#eff6ff"
                              : "transparent",
                          borderRadius: 8,
                          padding: "10px",
                          marginBottom: "6px",
                        }}
                      >
                        <div
                          ref={(el) => {
                            if (el) {
                              vehicleRefs.current[v.vehicleNo] = el;
                            }
                          }}
                        >
                          <List.Icon>
                            <HiOutlineTruck
                              color={getVehicleIconColor(v)}
                              size={18}
                            />
                          </List.Icon>
                          <List.Content>
                            <List.Header>{v.vehicleNo}</List.Header>
                            <List.Description style={{ color: "#6B7280" }}>
                              Lat: {v.latitude?.toFixed(2)} | Battery:{" "}
                              {v.batteryVoltage ?? "--"}V
                            </List.Description>
                          </List.Content>
                        </div>
                      </List.Item>
                    ))}
                </List>
              </div>
            </>
          )}
        </div>

        {/* Map */}
        <div
          style={{
            flex: selectedVehicle ? 1 : 0.45,
            height: "75vh",
            position: "relative",
            overflow: "hidden",
            borderRadius: "4px",
            margin: "4px 0px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div
            style={{
              height: "100%",
              width: "100%",
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            <FleetDashboardMap
              vehicles={
                selectedVehicle
                  ? [
                      {
                        lat: selectedVehicle.latitude,
                        lng: selectedVehicle.longitude,
                        vehicleNo: selectedVehicle.vehicleNo,
                        batteryVoltage: selectedVehicle.batteryVoltage,
                        fuelLevel: selectedVehicle.fuelLevel,
                        ignition: selectedVehicle.ignition,
                        mileage: selectedVehicle.mileage,
                        speed: selectedVehicle.speed,
                        image: selectedVehicle.image, // add image if available
                      },
                    ]
                  : vehicleLiveInfo
                      .filter((v) => {
                        const matchesFilter =
                          vehicleFilter === "all" ||
                          (vehicleFilter === "moving" && v.speed > 0) ||
                          (vehicleFilter === "stopped" &&
                            v.speed === 0 &&
                            !v.ignition) ||
                          (vehicleFilter === "onJob" && v.onJob);
                        return matchesFilter;
                      })
                      .map((v) => ({
                        lat: v.latitude,
                        lng: v.longitude,
                        vehicleNo: v.vehicleNo,
                        batteryVoltage: v.batteryVoltage,
                        fuelLevel: v.fuelLevel,
                        ignition: v.ignition,
                        mileage: v.mileage,
                        speed: v.speed,
                        image: v.image,
                      }))
              }
              popupVehicle={popupVehicle}
              setPopupVehicle={setPopupVehicle}
              selectedVehicle={selectedVehicle}
              setSelectedVehicle={setSelectedVehicle}
              onMarkerInfoClick={(vehicle) => {
                console.log("Info icon clicked:", vehicle);
                setSelectedVehicle(vehicle);
                fetchSelectedVehicleFinanceData(vehicle.vehicleNo);

                setTimeout(() => {
                  const vehicleElement = vehicleRefs.current[vehicle.vehicleNo];
                  if (vehicleElement && vehicleElement.scrollIntoView) {
                    vehicleElement.scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                    });
                  }
                }, 100);
              }}
            />
          </div>
        </div>

        {/* Right Panel */}
        <div
          style={{
            flex: selectedVehicle ? "0 0 440px" : "1",
            backgroundColor: "#fff",
            display: "flex",
            height: "75vh",
            flexDirection: "column",
            borderRadius: "4px",
            margin: "4px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
            transition: "width 0.4s ease",
            minWidth: "340px",
          }}
        >
          {selectedVehicle ? (
            isSelectLoading ? (
              <LoadingSpinner />
            ) : (
              <>
                {/* Vehicle Info Panel */}
                <div
                  style={{ padding: "20px", borderBottom: "1px solid #eee" }}
                >
                  <Header as="h4" style={{ marginBottom: 4 }}>
                    <span style={{ color: "#2563EB" }}>
                      {selectedVehicle.id}
                    </span>{" "}
                    <span style={{ color: "green", fontSize: "1.2em" }}>
                      {selectedVehicle.vehicleNo}
                    </span>
                  </Header>
                  <p style={{ margin: 0 }}>
                    <strong>{selectedVehicle.battery}</strong>{" "}
                    {selectedVehicle.ignition ? "Active" : "Stopped"}
                  </p>
                </div>

                <Tab
                  menu={{ secondary: true, pointing: true }}
                  panes={panes}
                  style={{ flex: 1, overflow: "hidden" }}
                />
              </>
            )
          ) : isLoading ? (
            <LoadingSpinner />
          ) : (
            <div style={{ padding: "10px" }}>
              <Header as="h4" style={{ marginBottom: 0 }}>
                Live Vehicle Data Analysis
              </Header>
              <div
                style={{
                  height: "65vh",
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                {/* Top Stats */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(6, 1fr)",
                    gap: "8px",
                    marginTop: "5px",
                  }}
                >
                  {[
                    {
                      label: "Active Vehicles",
                      value: allVehicleStats[0]?.totalActiveVehicles || 0,
                    },
                    {
                      label: "Arrival Ontime Job",
                      value: allVehicleStats[0]?.arrivalOntime || 0,
                    },
                    {
                      label: "Departure Ontime Job",
                      value: allVehicleStats[0]?.departureOntime || 0,
                    },
                    {
                      label: "GPS Vehicles",
                      value: allVehicleStats[0]?.availableVehicles || 0,
                    },
                    {
                      label: "Total Jobs",
                      value: allVehicleStats[0]?.totalActiveJobs || 0,
                    },
                    {
                      label: "Vehicles With Jobs",
                      value:
                        allVehicleStats[0]?.totalActiveVehicles -
                          allVehicleStats[0]?.idleVehicles || 0,
                    },
                  ].map((card, i) => (
                    <div
                      key={i}
                      style={{
                        backgroundColor: "#f9fafb",
                        borderRadius: "8px",
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                        padding: "16px",
                        textAlign: "center",
                        // maxWidth:"100px"
                      }}
                    >
                      <div
                        style={{
                          fontSize: "24px",
                          fontWeight: "bold",
                          color: "#1f2937",
                        }}
                      >
                        {card.value}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#4b5563",
                          marginTop: "4px",
                        }}
                      >
                        {card.label}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Vehicle Types & Fuel Consumption */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px",
                    marginTop: "8px",
                  }}
                >
                  {/* Vehicle Types Card */}
                  <div
                    style={{
                      background: "#fff",
                      padding: 10,
                      borderRadius: "4px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                      height: 420, // ⬅️ increased from 380
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Header as="h4" style={{ marginBottom: 0 }}>
                      Vehicle Status
                    </Header>
                    <div style={{ flex: 1 }}>
                      <ReactECharts
                        ref={chartRef}
                        option={vehicleStatusOptions}
                        style={{ height: "100%", width: "100%" }}
                        notMerge={true}
                        lazyUpdate={true}
                      />
                    </div>
                  </div>

                  {/* Fuel Consumption Card */}
                  <div
                    style={{
                      background: "#fff",
                      display: "flex",
                      flexDirection: "column",
                      padding: 10,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                      height: 420,
                      borderRadius: "4px",
                    }}
                  >
                    <Header as="h4" style={{ marginBottom: 0 }}>
                      Number of Vehicles by Speed Range
                    </Header>
                    <ReactECharts
                      option={speedDistributionOptions}
                      style={{
                        height: "100%",
                        width: "100%",
                        marginLeft: "5px",
                      }}
                    />
                  </div>
                </div>

                {/* Fleet Mileage Full Width */}
                <div
                  style={{
                    background: "#fff",
                    padding: 10,
                    display: "flex",
                    flexDirection: "column",
                    // gap: "8px",
                    marginTop: "8px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  }}
                >
                  <Header as="h4" style={{ marginBottom: 12 }}>
                    Top 5 Mileage
                  </Header>
                  <ReactECharts
                    option={mileageOptions}
                    style={{ height: "300px", width: "100%" }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "4px",
            margin: "4px",
            padding: "12px 14px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
          }}
      >
        <Header as="h4" style={{ marginBottom: 10 }}>
          Issues &amp; Work Orders Overview
        </Header>
        {isIssueWoStatsLoading ? (
            <LoadingSpinner height="80px" />
        ) : (
            issueWoStatRows.map(({ title, data }) => (
                <div key={title} style={{ marginBottom: 12 }}>
                  <p style={{ margin: "0 0 6px 2px", fontSize: 12, fontWeight: 600, color: "#374151" }}>
                    {title}
                  </p>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "8px" }}>
                    {issueWoStatusCards.map(({ key, label, color }) => (
                        <div
                            key={key}
                            style={{
                              backgroundColor: "#f9fafb",
                              borderRadius: "8px",
                              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                              padding: "16px",
                              textAlign: "center",
                              borderTop: `3px solid ${color}`,
                            }}
                        >
                          <div style={{ fontSize: "24px", fontWeight: "bold", color: "#1f2937" }}>
                            {data?.[key] ?? 0}
                          </div>
                          <div style={{ fontSize: "12px", color: "#4b5563", marginTop: "4px" }}>
                            {label}
                          </div>
                          {key === "closedThisMonth" && (
                              <div style={{ fontSize: "10px", color: "#9CA3AF", marginTop: 2 }}>
                                Current month
                              </div>
                          )}
                        </div>
                    ))}
                  </div>
                </div>
            ))
        )}
      </div>

      <div
        style={{
          backgroundColor: "#fff",
          display: "flex",
          flexDirection: "column",
          borderRadius: "4px",
          margin: "4px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
          minHeight: isSelectedVehicleFinanceView ? "260px" : "70vh",
        }}
      >
        <div
          style={{
            padding: "10px 14px",
            borderBottom: "1px solid #eee",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <div>
              <Header as="h4" style={{ marginBottom: 2 }}>
                {isSelectedVehicleFinanceView && selectedVehicle
                  ? `Vehicle Finance Details - ${selectedVehicle.vehicleNo}`
                  : "Vehicle Finance Summary"}
              </Header>

              <p style={{ margin: 0, fontSize: 12, color: "#6B7280" }}>
                Finance performance from {fromDate || "--"} to {toDate || "--"}
                {userCurrencyName ? ` | Currency: ${userCurrencyName}` : ""}
              </p>
            </div>

            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {isSelectedVehicleFinanceView ? (
                <Button
                  icon
                  size="small"
                  title="Cancel Selected Vehicle Finance View"
                  onClick={handleCancelSelectedVehicleFinanceView}
                >
                  <Icon name="cancel" color="red" />
                </Button>
              ) : (
                <>
                  <TextField
                    label="From Date"
                    type="date"
                    size="small"
                    value={fromDate || ""}
                    onChange={(e) => setFromDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    style={{ width: "160px" }}
                  />

                  <TextField
                    label="To Date"
                    type="date"
                    size="small"
                    value={toDate || ""}
                    onChange={(e) => setToDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    style={{ width: "160px" }}
                  />

                  <Button
                    icon
                    size="small"
                    title="Apply Filter"
                    onClick={handleApplyFinanceDateFilter}
                    loading={isFinanceLoading}
                    disabled={isFinanceLoading}
                  >
                    <Icon name="filter" color="black" />
                  </Button>

                  <Button
                    icon
                    size="small"
                    title="Reset Filter"
                    onClick={handleResetVehicleFinanceModalFilter}
                    loading={isFinanceLoading}
                    disabled={isFinanceLoading}
                  >
                    <Icon name="undo" color="black" />
                  </Button>

                  <Button
                    icon
                    size="small"
                    title="Expand"
                    onClick={handleOpenFinanceModal}
                  >
                    <Icon name="expand arrows alternate" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {isFinanceLoading || isSelectedVehicleFinanceLoading ? (
          <LoadingSpinner height="40vh" />
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
                gap: "8px",
                padding: "10px",
              }}
            >
              {(isSelectedVehicleFinanceView
                ? selectedVehicleFinanceCards
                : financeSummaryCards
              ).map((card, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: isSelectedVehicleFinanceView
                      ? "#eff6ff"
                      : "#f9fafb",
                    borderRadius: "8px",
                    padding: "10px 8px",
                    textAlign: "center",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    minWidth: 0,
                    border: isSelectedVehicleFinanceView
                      ? "1px solid #bfdbfe"
                      : "1px solid transparent",
                  }}
                >
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      color: "#1f2937",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={String(card.value)}
                  >
                    {card.value}
                  </div>

                  <div
                    style={{
                      fontSize: "10px",
                      color: "#6B7280",
                      marginTop: 4,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={card.label}
                  >
                    {card.label}
                  </div>
                </div>
              ))}
            </div>

            {isSelectedVehicleFinanceView ? (
              selectedVehicleFinanceData.length === 0 && (
                <div
                  style={{
                    margin: "10px",
                    padding: "20px",
                    backgroundColor: "#f9fafb",
                    borderRadius: "8px",
                    color: "#6B7280",
                    textAlign: "center",
                  }}
                >
                  No finance details found for selected vehicle
                </div>
              )
            ) : (
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  height: "100%",
                  padding: "10px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {financeSummaryData?.length === 0 ? (
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#6B7280",
                    }}
                  >
                    No finance summary records found
                  </div>
                ) : (
                  <>
                    <p
                      style={{
                        margin: "0 0 8px 0",
                        fontSize: 12,
                        color: "#6B7280",
                      }}
                    >
                      Showing top {financeSummaryChartData.length} vehicles by
                      total balance.
                    </p>

                    <ReactECharts
                      option={financeSummaryChartOptions}
                      style={{ flex: 1, width: "100%", minHeight: 0 }}
                    />
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <div
        style={{
          backgroundColor: "#fff",
          display: "flex",
          borderRadius: "4px",
          margin: "4px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
        }}
      >
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              height: "70vh",
              width: "100%",
            }}
          >
            <div
              style={{
                padding: "5px 10px",
                marginBottom: 0,
                display: "flex",
                alignItems: "space-between",
                justifyContent: "space-between",
              }}
            >
              <div>
                <Header as="h4" style={{ marginBottom: 0 }}>
                  GPS Offline Vehicles
                </Header>
              </div>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Filter by Vehicle No"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                style={{ width: "250px" }}
              />
            </div>

            {/* --- Filter Input --- */}
            <div style={{ padding: "0 20px 10px" }}></div>

            <TableContainer component={Paper} style={{ flex: 1 }}>
              <Table stickyHeader aria-label="GPS Offline Vehicles">
                <TableHead>
                  <TableRow>
                    <TableCell>Vehicle No</TableCell>
                    <TableCell align="right">TerminalId</TableCell>
                    <TableCell align="right">Last Data Received</TableCell>
                    <TableCell align="right">Transporter</TableCell>
                    <TableCell align="right">Mileage</TableCell>
                    {/* <TableCell align="right">GPS Provider</TableCell> */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.map((row) => (
                    <TableRow
                      key={row.vehicleNo}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}
                    >
                      <TableCell component="th" scope="row">
                        {row.vehicleNo}
                      </TableCell>
                      <TableCell align="right">{row.terminalId}</TableCell>
                      <TableCell align="right">{row.dateTime}</TableCell>
                      <TableCell align="right">
                        {row.vehTransporter ? row.vehTransporter : "N/A"}
                      </TableCell>
                      <TableCell align="right">
                        {row.mileage ? row.mileage : "N/A"}
                      </TableCell>
                      {/* <TableCell align="right">
                        {row.gpsServiceProvider}
                      </TableCell> */}
                    </TableRow>
                  ))}
                  {filteredData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        No matching records
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}
      </div>

      <Modal
        ref={modalRef}
        minWidth={"80vw"}
        minHeight={"95vh"}
        open={isModalOpen}
        onCloseCleanUp={handleCollapseFinanceTable}
        onClose={handleCloseFinanceModal}
        closeButton={false}
      >
        <div
          style={{
            height: "90%",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#fff",
          }}
        >
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid #eee",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <div>
              <Header as="h4" style={{ marginBottom: 2 }}>
                Vehicle Finance Details
              </Header>

              <p style={{ margin: 0, fontSize: 12, color: "#6B7280" }}>
                Finance records from {fromDate || "--"} to {toDate || "--"}
                {userCurrencyName ? ` | Currency: ${userCurrencyName}` : ""}
              </p>
            </div>

            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <TextField
                label="From Date"
                type="date"
                size="small"
                value={fromDate || ""}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                style={{ width: "160px" }}
              />

              <TextField
                label="To Date"
                type="date"
                size="small"
                value={toDate || ""}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                style={{ width: "160px" }}
              />

              <TextField
                label="Vehicle Nos"
                size="small"
                placeholder="WPKD-4774, SPTY-7899"
                value={financeVehicleNos}
                onChange={(e) => setFinanceVehicleNos(e.target.value)}
                style={{ width: "300px" }}
              />

              <Button
                icon
                size="small"
                title="Apply Filter"
                onClick={handleApplyVehicleFinanceTableFilter}
                loading={isFinanceLoading}
                disabled={isFinanceLoading}
              >
                <Icon name="filter" color="black" />
              </Button>

              <Button
                icon
                size="small"
                title="Reset Filter"
                onClick={handleResetVehicleFinanceModalFilter}
                loading={isFinanceLoading}
                disabled={isFinanceLoading}
              >
                <Icon name="undo" color="black" />
              </Button>

              <Button
                icon
                size="small"
                title="Close"
                onClick={handleCloseFinanceModal}
              >
                <Icon name="compress arrows alternate" />
              </Button>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
              gap: "8px",
              padding: "10px",
            }}
          >
            {[
              {
                label: "No. of Vehicles",
                value: new Set(
                  vehicleFinanceData
                    .map((row) => row.vehVehicleno)
                    .filter(Boolean),
                ).size,
              },
              {
                label: "No. of Jobs",
                value: vehicleFinanceData.reduce(
                  (sum, row) => sum + Number(row.total || 0),
                  0,
                ),
              },
              {
                label: `Income${userCurrencyName ? ` (${userCurrencyName})` : ""}`,
                value: formatAmount(
                  vehicleFinanceData.reduce(
                    (sum, row) => sum + Number(row.chhCost || 0),
                    0,
                  ),
                  false,
                ),
              },
              {
                label: `Deductions${userCurrencyName ? ` (${userCurrencyName})` : ""}`,
                value: formatAmount(
                  vehicleFinanceData.reduce(
                    (sum, row) => sum + Number(row.deductions || 0),
                    0,
                  ),
                  false,
                ),
              },
              {
                label: `Maintenance${userCurrencyName ? ` (${userCurrencyName})` : ""}`,
                value: formatAmount(
                  vehicleFinanceData.reduce(
                    (sum, row) => sum + Number(row.chhMaintenance || 0),
                    0,
                  ),
                  false,
                ),
              },
              {
                label: `Balance${userCurrencyName ? ` (${userCurrencyName})` : ""}`,
                value: formatAmount(
                  vehicleFinanceData.reduce(
                    (sum, row) => sum + Number(row.totCost || 0),
                    0,
                  ),
                  false,
                ),
              },
            ].map((card, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: "#f9fafb",
                  borderRadius: "8px",
                  padding: "10px 8px",
                  textAlign: "center",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    color: "#1f2937",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {card.value}
                </div>

                <div
                  style={{
                    fontSize: "10px",
                    color: "#6B7280",
                    marginTop: 4,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {card.label}
                </div>
              </div>
            ))}
          </div>

          <div style={{ flex: 1, minHeight: 0 }}>
            {isFinanceLoading ? (
              <LoadingSpinner />
            ) : (
              <TableContainer component={Paper} style={{ height: "100%" }}>
                <Table
                  stickyHeader
                  size="small"
                  aria-label="Vehicle Finance Details"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Vehicle No</TableCell>
                      <TableCell align="right">Jobs</TableCell>
                      <TableCell align="right">
                        Income {userCurrencyName ? `(${userCurrencyName})` : ""}
                      </TableCell>
                      <TableCell align="right">
                        Deductions{" "}
                        {userCurrencyName ? `(${userCurrencyName})` : ""}
                      </TableCell>
                      <TableCell align="right">
                        Maintenance{" "}
                        {userCurrencyName ? `(${userCurrencyName})` : ""}
                      </TableCell>
                      <TableCell align="right">
                        Balance{" "}
                        {userCurrencyName ? `(${userCurrencyName})` : ""}
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {vehicleFinanceData.map((row) => (
                      <TableRow key={row.jobVehicle || row.vehVehicleno}>
                        <TableCell component="th" scope="row">
                          <strong>{row.vehVehicleno || "N/A"}</strong>
                        </TableCell>

                        <TableCell align="right">{row.total || 0}</TableCell>

                        <TableCell align="right">
                          {formatAmount(row.chhCost, false)}
                        </TableCell>

                        <TableCell align="right">
                          {formatAmount(row.deductions, false)}
                        </TableCell>

                        <TableCell align="right">
                          {formatAmount(row.chhMaintenance, false)}
                        </TableCell>

                        <TableCell align="right" style={{ fontWeight: "bold" }}>
                          {formatAmount(row.totCost, false)}
                        </TableCell>
                      </TableRow>
                    ))}

                    {vehicleFinanceData.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No vehicle finance records found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FleetDashboard;
